import { Box, Button, Group, Stack, Text } from "@mantine/core";
import { FC, useState } from "react";
import { ReactComponent as RightIcon } from "assets/expand.svg";
import { ReactComponent as PalleteIcon } from "assets/Palette.svg";
import FilterModal from "./FilterModal";
import { SPECIALTY_COLOR_MAP, STATUS_COLOR_MAP, TYPE_TITLE_MAP } from "../consts";
import { ColorType } from "../ColorsPanel";

interface Props {
  onApplyFilter: (f: FilterControl) => void;
  onClose: () => void;
  initFilterControl?: FilterControl;
  colorControl?: ColorType;
}

export type FilterType = "last-check-in" | "status" | "until-follow-up" | "specialty-type";

export const END_CHECK_IN_SCALE = 2724;

export interface FilterControl {
  minDaysSinceLastCheckIn: number | null;
  maxDaysSinceLastCheckIn: number | null;
  isCheckInFilterActive: boolean;
  minDaysUntilFollowUp: number | null;
  maxDaysUntilFollowUp: number | null;
  status: string[] | null;
  specialtyType: string[] | null;
}

export interface FilterComponentProps {
  onFilterControlChange: (fc: Partial<FilterControl>) => void;
  filterControl: Partial<FilterControl>;
}

export const DEFAULT_VALUES: FilterControl = {
  minDaysSinceLastCheckIn: 0,
  maxDaysSinceLastCheckIn: END_CHECK_IN_SCALE,
  isCheckInFilterActive: false,
  minDaysUntilFollowUp: null,
  maxDaysUntilFollowUp: null,
  status: Array.from(STATUS_COLOR_MAP.keys()),
  specialtyType: Array.from(SPECIALTY_COLOR_MAP.keys()),
}

export const isFilterActive = (fc?: FilterControl): boolean => (
  Object.entries(fc || {}).some(([k, v]) => {
    if (k === "isCheckInFilterActive") {
      if (v) {
        return true
      }
      return false;
    }
    if (k === "status") {
      if (v.length !== STATUS_COLOR_MAP.size) {
        return true;
      }
      return false;
    }
    if (k === "specialtyType") {
      if (v.length !== SPECIALTY_COLOR_MAP.size) {
        return true;
      }
      return false;
    }
    return false;
  })
);

const FiltersPanel: FC<Props> = ({
  onApplyFilter,
  onClose,
  initFilterControl,
  colorControl
}) => {
  const [filterControl, setFilterControl] = useState<FilterControl>(initFilterControl || DEFAULT_VALUES);
  const [activeFilter, setActiveFilter] = useState<FilterType | null>(null);

  const resetFilters = () => {
    onApplyFilter(DEFAULT_VALUES);
    setFilterControl(DEFAULT_VALUES);
  }

  return (
    <Box pos="relative" h="90%">
      <FilterModal
        filterControl={filterControl}
        setFilterControl={setFilterControl}
        activeFilter={activeFilter}
        onApplyFilter={(f) => {
          if (f) {
            onApplyFilter(f);
          }
          setActiveFilter(null);
        }}
        onClose={() => {
          onClose();
        }} />
      <Text fz="sm" my="sm">
        Show <b>only pins</b> on map by:
      </Text>
      <Stack justify="space-between"
        style={{
          boxSizing: "border-box",
          height: "100%",
        }}>
        <Stack gap={0} ml="15px">
          <Group
            justify="space-between"
            h={40}
            style={{
              borderBottom: "1px solid #F0F0F0",
            }}
            onClick={() => setActiveFilter("last-check-in")}>
            <Text
              fw="bold"
              lh="40px">
              {TYPE_TITLE_MAP.get("last-check-in")}
            </Text>
            <Box>
              {colorControl === "last-check-in" ? (
                <PalleteIcon width={24} />
              ) : null}
              <RightIcon width={24} style={{ marginBottom: "2px" }} />
            </Box>
          </Group>
          <Group
            justify="space-between"
            h={40}
            style={{
              borderBottom: "1px solid #F0F0F0",
            }}
            onClick={() => setActiveFilter("status")}>
            <Text
              fw="bold"
              lh="40px">
              {TYPE_TITLE_MAP.get("status")}
            </Text>
            <Box>
              {colorControl === "status" ? (
                <PalleteIcon width={24} />
              ) : null}
              <RightIcon width={24} style={{ marginBottom: "2px" }} />
            </Box>
          </Group>
          <Group
            justify="space-between"
            h={40}
            style={{
              borderBottom: "1px solid #F0F0F0",
            }}
            onClick={() => setActiveFilter("specialty-type")}>
            <Text
              fw="bold"
              lh="40px">
              {TYPE_TITLE_MAP.get("specialty-type")}
            </Text>
            <Box>
              {colorControl === "specialty-type" ? (
                <PalleteIcon width={24} />
              ) : null}
              <RightIcon width={24} style={{ marginBottom: "2px" }} />
            </Box>
          </Group>
        </Stack>
        <Button
          bg="#FF9D00"
          fullWidth
          style={{ boxSizing: "border-box" }}
          onClick={() => {
            resetFilters();
          }}>
          Reset Filters
        </Button>
      </Stack>
    </Box>
  );
};

export default FiltersPanel;